import type { FieldErrors, FieldValues, Resolver } from 'react-hook-form'
import { z } from 'zod'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PASSWORD_PATTERN = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/

/**
 * Minimal Zod resolver for React Hook Form. Error `message` values are
 * catalog keys, never user-facing copy, so both locales share one schema.
 */
export function zodResolver<T extends FieldValues>(
  schema: z.ZodType<T>,
): Resolver<T> {
  return async (values) => {
    const result = schema.safeParse(values)
    if (result.success) {
      return { values: result.data, errors: {} }
    }

    const errors: Record<string, unknown> = {}
    for (const issue of result.error.issues) {
      const path = issue.path.map((part) => String(part))
      const key = path.length > 0 ? path : ['root']
      let cursor: Record<string, unknown> = errors
      for (const segment of key.slice(0, -1)) {
        const next = cursor[segment]
        if (typeof next !== 'object' || next === null) {
          cursor[segment] = {}
        }
        cursor = cursor[segment] as Record<string, unknown>
      }
      const leaf = key[key.length - 1] ?? 'root'
      if (!cursor[leaf]) {
        cursor[leaf] = { type: issue.code, message: issue.message }
      }
    }

    return { values: {}, errors: errors as FieldErrors<T> }
  }
}

export function maxBirthDate(now: Date = new Date()): string {
  const cutoff = new Date(
    Date.UTC(now.getUTCFullYear() - 18, now.getUTCMonth(), now.getUTCDate()),
  )
  return cutoff.toISOString().slice(0, 10)
}

export function isAdult(birthDate: string, now: Date = new Date()): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) return false
  const date = new Date(`${birthDate}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) return false
  return date.getTime() <= new Date(`${maxBirthDate(now)}T00:00:00Z`).getTime()
}

export const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .regex(EMAIL_PATTERN, 'auth.login.validation'),
  password: z.string().min(1, 'auth.login.validation'),
})

export type LoginValues = z.infer<typeof loginSchema>

export const forgotPasswordSchema = z.object({
  email: z
    .string()
    .trim()
    .regex(EMAIL_PATTERN, 'auth.login.forgotValidation'),
})

export const registerSchema = z
  .object({
    firstName: z
      .string()
      .trim()
      .min(1, 'auth.validation.firstNameRequired')
      .max(80, 'auth.validation.nameTooLong'),
    lastName: z
      .string()
      .trim()
      .min(1, 'auth.validation.lastNameRequired')
      .max(80, 'auth.validation.nameTooLong'),
    email: z
      .string()
      .trim()
      .toLowerCase()
      .regex(EMAIL_PATTERN, 'auth.validation.emailInvalid'),
    birthDate: z
      .string()
      .min(1, 'auth.validation.birthDateRequired')
      .refine((value) => isAdult(value), 'auth.validation.adult'),
    password: z
      .string()
      .regex(PASSWORD_PATTERN, 'auth.validation.passwordRules'),
    confirmPassword: z.string().min(1, 'auth.validation.passwordMismatch'),
    bio: z.string().max(1000, 'auth.validation.bioTooLong'),
    acceptedTerms: z
      .boolean()
      .refine((value) => value, 'auth.validation.termsRequired'),
  })
  .refine((values) => values.password === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'auth.validation.passwordMismatch',
  })

export type RegisterValues = z.infer<typeof registerSchema>

export const googleRegisterSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, 'auth.validation.firstNameRequired')
    .max(80, 'auth.validation.nameTooLong'),
  lastName: z
    .string()
    .trim()
    .min(1, 'auth.validation.lastNameRequired')
    .max(80, 'auth.validation.nameTooLong'),
  birthDate: z
    .string()
    .min(1, 'auth.validation.birthDateRequired')
    .refine((value) => isAdult(value), 'auth.validation.adult'),
  acceptedTerms: z
    .boolean()
    .refine((value) => value, 'auth.validation.termsRequired'),
})

export type GoogleRegisterValues = z.infer<typeof googleRegisterSchema>

export const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .regex(PASSWORD_PATTERN, 'auth.validation.passwordRules'),
    confirmPassword: z.string().min(1, 'auth.validation.passwordMismatch'),
  })
  .refine((values) => values.password === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'auth.validation.passwordMismatch',
  })

export type ResetPasswordValues = z.infer<typeof resetPasswordSchema>
